import {
    UnprocessedOrderType,
    PrivacyPhoneStatusCode,
    OrderStatusCode,
    LogisticsStatusCode,
    PayStatusCode,
    LogisticsTypeCode,
    PickTypeCode,
    OrderPrepareStatusCode,
    SelfServiceCode,
    PayTypeCode,
    OrderBusinessType,
    OrderCommonInfo,
    RawOrderBaseInfo,
    DispatchExceptionCode,
} from '../shared/types';

/** 退款相关的各类提示文案 */
type RefundTips = {
    tips_after_reject: string;
    tips_double_complain: string;
    tips_one: string[];
    tips_two: string[];
    tips_three: string[];
};

/** 商品详情 */
type FoodDetail = {
    /** 商品名称 */
    food_name: string;
    /** 购买数量 */
    count: number;
    /** 购买单位 */
    unit: string;
    wm_food_id: number;
    origin_food_price: number;
    box_num: number;
    hasPackageDetails: number;
    id: number;
    tagWeight: number;
    foodTagName: string;
    cart_id: number;
    packageDetail: any[];
    food_price: number;
    locator_code: string;
    upc_code: string;
    foodTagId: number;
    countBak: number;
    source_food_code: string;
    box_price: number;
    /** 折扣比例，如果为空字符则表示非折扣菜 */
    discountPoint: string;
};

/** 订单下的一个包裹，一个订单可以有多个包裹，一个包裹又可以有多个商品类型 */
type CartDetail = {
    cartAmount: number;
    /** 包裹名称 */
    cartName: string;
    /** 包裹中的具体商品列表 */
    details: FoodDetail[];
};

/** 订单参与的活动信息 */
type ActivityDetail = {
    actDesc: string;
    actPrice: number;
    giftName: string;
    count: number;
};

/**
 * 餐损险信息
 */
type OrderInsuranceInfo = {
    /** 是否生效 */
    effect: number;
    /** 保险状态描述 */
    desc: string;
    /** 保险状态 */
    status: InsuranceStatusCode;
    /** 保险名称 */
    name: string;
    /** 理赔结果描述信息 */
    claimDesc: string;
};

/** 配送费详情 */
type ShippingFeeDetailVo = {
    /** 配送费(基础配送费+临时加价) */
    shippingFee: number;
    /** 基础配送费 */
    shippingFeeBase: number;
    /** 优惠券金额 */
    couponAmount: number;
    /** 活动名称 */
    activityName: string | null;
    /** 活动金额 */
    activityAmount: number;
    /** 临时加价金额 */
    extraFee: number;
    /** 小费 */
    tipAmount: number;
    /** 实付 */
    payAmount: number;
    /** 支付方式:  1账户余额  2账期结束 3订单款 */
    payType: number;
};

/** 泛自配商家的配送方式（自配、跑腿、三方） */
type Distributor = {
    carrierCode: string;
    /** 拼接好的前端展示名称 */
    carrierConnectLogisticsName: string;
    /** 服务商名称(三方) */
    carrierName: string;
    /** 商家配送方式名称，如果是三方配则该字段是服务包的名字 */
    logisticsName: string;
    logisticsCode: LogisticsTypeCode;
    logisticsId: number;
    /** 商家的配送方式类型：1=自配，2=美配，3=三方配送 4=聚合配送 */
    logisticsType: number;
    priority: number;
    /** 具体的配送方式Code */
    specificLogisticsCode: string;
    specificLogisticsId: number;
    /** 是否展示资源位角标，0不展示，1展示 */
    smallMarkType: 0 | 1;
    /** 按钮展示类型：0正常显示，1置灰不可点击 */
    displayType: 0 | 1;
};

/**
 * 订单配送信息
 * 来自接口 /v2/order/receive/processed/r/distribute/list/v2
 */
type OrderDeliveryInfo = {
    assignDegrade: boolean;
    /** 分配骑手的时间 */
    assignTime: number;
    /** 系统当前时间戳（秒） */
    currentTime: number;
    delay_push_second: number;
    /** 配送取消原因 */
    dispatchFailReason: string;
    dispatchTeam: null;
    dispatch_cancel_allow: number;
    /** 骑手姓名 */
    dispatcher_name: '';
    /** 骑手电话 */
    dispatcher_phone: null | string;
    distributorList: null | Distributor[];
    hasJuheAndZB: false;
    /** 是否混合快送，当配送code为3001，且该字段为true时，表示混合送快送。📢：有实际运力回调之后才会将hhks置为true */
    hhKS: boolean;
    /** 是否快送，前期遗留的字段（前期快送没有自己的logistics_code，在logistics_code===1003的情况下，额外使用isKS字段来表示快送），后续快送有了自己的logistics_code 2002 */
    isKS: 1 | 0;
    is_third_part_shipping: number;
    lastUse: null | Distributor;
    latestLogisticsLogDesc: string;
    latest_delivery_time: number;
    /** 配送类型 Code */
    logistics_code: LogisticsTypeCode | null;
    /** 配送类型 ID */
    logistics_id: number;
    /** 配送类型名称 */
    logistics_name: string;
    /** 配送状态 */
    logistics_status: LogisticsStatusCode;
    /** 站点（备用）电话 */
    org_emergency_phone: null | string;
    /** 站点（站长）电话 */
    org_leader_phone: null | string;
    /** 配送费金额 */
    poi_shipping_fee: number | null;
    preCheckJuHeMsg: null;
    /** 是否跑腿灰度商家 */
    ptGray: 0 | 1;
    /** 是否允许自配送 */
    self_dispatch: 0 | 1;
    sendOutTime: number;
    /** 跑腿灰度、三方配送费详情 */
    shippingFeeDetailVo: ShippingFeeDetailVo | null;
    subLogisticsCode: number;
    supportUnionDispatcher: 0 | 1;
    third_part_shipping_name: null;
    third_part_shipping_phone: null;
    /** 配送小费金额 */
    tip_amount: number;
    /** logisticsStatus=0时，为【当前时间-商家发起配送时间】其它情况不设置 */
    waiting_time: number;
    wm_order_id: number;
    /** 门店ID */
    wm_poi_id: number;
    /** 企客发配送降级（降级时展示”自己送餐”） */
    qiKeDowngrade: boolean;
    /**
     * 跑腿延迟发配送时间（单位：秒）
     * 【跑腿商家未设置延迟发配送】 or 【其它配送方式】为0
     */
    runLegDelayTime: number;
    /** 生效中的权益 */
    logisticsRight?: PaotuiOrderRightsInfo;
    /** 预订单压单文案 */
    preOrderPressureInfo: string;
    /** 出餐慢压单文案 */
    slowOrderPressureInfo: string;
    /** 拼好饭拼单子单配送模块提示文案 */
    phfOperationContent: string;
    /** 大订单文案提示 */
    bigOrderTip: string;
    /** 是否是餐车订单 */
    dingCarType: 0 | 1;
    /** 餐车订单配送按钮展示控制 */
    dingCarButton: boolean;
    /** 餐车订单配送按钮展示文案 */
    dingCarButtonDesc: string;
};

/**
 * 订单其他信息（门店所属城市，订单打印次数，订单取消原因，下单次数，备餐状态等）
 * 来自接口 /v2/order/receive/processed/r/orderAsyncInfos/v3
 */
type OrderOtherInfo = {
    /** 出餐按钮可操作的时间，时间戳（s） */
    buttonTimeLimit: number;
    /** 订单取消原因 */
    cancel_reason: string;
    category: number;
    city_id: number;
    /** 门店所属城市 */
    city_name: string;
    /** 系统当前时间，时间戳（s） */
    current_time: number;
    /** 配送异常类型 1-6 */
    dispatchExceptionType: DispatchExceptionCode;
    // 配送异常自定义文案
    phsDegrade: string;
    /** 此订单是否由于配送异常，而转过自配 */
    displaySelfDispatchStatus: boolean;
    /** 出餐时间戳，时间戳（秒）**命名非常有迷惑性** */
    prepareDuration: number;
    /** 备餐状态 */
    prepareStauts: OrderPrepareStatusCode;
    /** 拼好饭拼单备餐提示文案 */
    phfFoodDoneContent: string;
    /** 订单小票打印的次数 */
    print_cnt: number;
    /** 订单标签(团餐)打印的次数 */
    labelPrintCount: number;
    /** 是否展示隐私号录音提示 */
    privacyRecord: boolean;
    /** 用户下单次数，非0表示老客 */
    purchase_number: number;
    /** 退款详细类型，2表示极速退，1表示超时退，其它表示普通退 */
    refundBizType: number;
    /** 是否由'出餐宝'确认出餐 */
    scanGun: boolean;
    /** 建议出餐时间（分钟） */
    sendOutTime: number;
    /** 是否展示'投诉骑手'按钮 */
    showDeliveryException: boolean;
    /** 是否展示'自己配送'按钮（非常规自配，此处是订单配送异常之后的临时自配送措施） */
    showSlefDeliveryButton: boolean;
    wm_order_id: number;
    wm_order_id_view_str: string;
    wm_poi_id: number;
    notReportCompleteText?: string;
    /** 跑腿配送资源位角标信息 */
    zbResourceInfo?: {
        /** 开始展示的时间戳 */
        start_time: number;
        /** 结束展示的时间戳 */
        end_time: number;
        /** 图片地址 */
        pic_url: string;
        /** 图片类型： 0资源位，1权益 */
        type: 0 | 1;
    };
    foodDoneInfo?: {
        /** 出餐主提示及计时文案 */
        title: string;
        /** 出餐完成按钮文案 */
        buttonName: string;
        /** 是否展示出餐完成按钮 */
        foldButtonFlag: number;
        /** 出餐相关提示 */
        content: string;
        /** 天平四期 灰度 */
        grayFlag: boolean;
        /** 是否 骑手取餐但商家未上报出餐 */
        titleType: number;
        /** 按钮tag */
        tag: string;
    };
    /**
     * @description 出餐新增考核, 可能会为空值
     * @documentation https://km.sankuai.com/collabpage/1898306897
     */
    mealFoodDoneVO?: {
        basicVO: BasicVO;
        buttonVO: ButtonVO | null;
        contentVOList: ContentVO[];
    };
};

/**
 * 订单用户信息（是否收藏店铺，是否美团外卖会员等）
 */
type OrderUserInfo = {
    /** 用户是否收藏店铺 1 已收藏，0 未收藏 */
    favorites: 0 | 1;
    /** 是否美团外卖会员 */
    wmMember: boolean;
    wmOrderViewId: string;
};

/**
 * 订单距离信息
 */
type OrderDistanceInfo = { distance: number; wm_order_id_view_str: string };

/** 订单图片信息
 * 包含收餐人电话字符串的图片
 * 包含收餐人地址字符串的图片
 */
type OrderImgInfo = {
    recipient_phone: string;
    recipient_address: string;
};

/** 结算信息中的赠品信息 */
type GiftDetail = {
    giftName: string;
    giftActType: string;
    giftAmount: number;
    giftCount: number;
};

type ChargeItem = {
    /** 收费项名称 */
    chargeDetailName: string;
    /** 收费项金额 */
    chargeDetailMoney: string;
    /** 样式ID */
    styleId: string;
    /** 嵌套的子收费项 */
    item?: ChargeItem[];
};

/** 结算信息项 */
type SettlementItem = {
    /** 是否展示问号 */
    showRemind: boolean;
    /** 问号展示的位置，0跟在收费名称后边，1跟在费用后边 */
    showRemindStyle: 0 | 1;
    /** 分割线样式，0不分割，1上方分隔，2下方分隔，3上下都分隔 */
    separateStyle: 0 | 1 | 2 | 3;
    /** 费用计算公式 */
    calculationRules: string;
    /** hover 问号时展示的文案(目前仅有青山计划用到) */
    toast: string;
    /** 收费项名称 */
    chargeName: string;
    /** 弹框标题文案（如果有） */
    popupTitle: string;
    /** 收费项扩展文案（红色高亮） */
    chargeNameExtend?: string;
    /** 收费项金额 */
    totalCharge: string;
    /** 弹窗的总收费名称 */
    popupTotalChargeName: string;
    /** 弹窗收费的总金额 */
    popupTotalCharge: string;
    /** 弹框副标题 */
    popupSubTitle: string;
    /** 弹窗收费的(副)总金额，主要展示划线价 */
    popupTotalChargeExtension: string;
    /** 弹窗收费的(副)总金额样式， 0为正常格式，1为中划线*/
    popupTotalChargeExtensionStyleId: string;
    /** 收费规则 */
    chargeRules: string;
    /** 收费规则对应的名称 */
    chargeRulesName: string;
    /** 收费项明细列表 */
    item: ChargeItem[];
    /** 样式id */
    styleId: string;
    /** 子收费项（PHF：商品金额合计、商家费用合计） */
    subSettleDynamicList?: SettlementItem[];
};

/** 订单结算信息 */
type OrderChargeInfo = {
    wmPoiId: number;
    wmOrderId: number;
    wmOrderViewId: number;
    wmOrderViewIdStr: string;
    wmChargeId: number;
    /** 赠品信息 */
    giftDetails: null | GiftDetail[];
    activityDetails: null | any[];
    commisionDetails: null | any[];
    foodAmount: number;
    activityAmount: number;
    commisionAmount: number;
    settleAmount: number;
    shippingAmount: number;
    userPayAmount: number;
    offlineOrderSkPayAmount: number;
    shippingType: LogisticsTypeCode;
    utime: number;
    totalBefore: number;
    totalAfter: number;
    riderPayment: null | string;
    /** 在线支付用户实际支付金额 */
    userPayTotalAmount: number;
    donateAmount: number;
    activity: null | any[];
    /** 降级状态标识,0非降级使用结算数据 1降级使用订单中心数据 */
    degreeStatus: 0 | 1;
    /** 闪购收费模式 1 旧收费模式 2 新收费模式 */
    chargeMode: 1 | 2;
    /** 闪购 履约服务费 */
    agreementAmount: number;
    /** 是否展示用户享受优惠 */
    showReduceFee: boolean;
    /** 结算公式title */
    formulaDisplayTitle: string;
    /** 结算公式 */
    formulaDisplay: string;
    settleDynamic: SettlementItem[];
    riderPay: boolean;
    settleAmountError: boolean;
    settleAmountDegrade?: boolean;
    degradeSettleName: string;
    degradeSettleAmount: string;
};

/**
 * 用户赔付/客服退款 信息
 */
type OrderUserCompensateInfo = {
    /** 客服退款id */
    refundRecordId: number;
    orderViewId: string;
    /** 赔偿金额，**单位：分** */
    amount: number;
    /** 一级原因code */
    firstReasonCode: number;
    /** 一级原因详情 */
    firstReason: string;
    /** 二级原因code */
    secondReasonCode: number;
    /** 二级原因详情 */
    secondReason: string;
    /** 赔付成功发起时间，unix时间戳 */
    executeTime: number;
    /** 图片列表 */
    attachList: string[];
    /** 备注 */
    remark: string;
};

/** 全部订单公用的顾客赔偿相关文案 */
type SharedUserCompensateRule = {
    refundRule1: string;
    refundRule2: string;
    refundAppeal1: string;
    refundAppeal2: string;
    deductionType1: string;
    deductionType2: string;
    appealTips: string;
};

/**
 * 催单记录
 *
 * 一个订单可以多次催单
 */
type OrderRemindRecord = {
    /** 催单次数 */
    reminder_count: number;
    /** 接收人名称 */
    recipient_name: string;
    /** 用户发起催单时间戳 */
    reminder_time: number;
    wm_order_id_view: number;
    /** 订单是否有效 */
    stale: number;
    order_time_fmt: string;
    wm_order_id: number;
    status: OrderStatusCode;
    /** 催单状态 */
    reminder_type: number;
    /** 发起催单时间字符串  格式 2016-01-03 18:12 */
    reminder_time_fmt: string;
    /** 催单ID */
    reminder_id: number;
    /** 门店名称 */
    poi_name: string;
    /** 失效原因 */
    invalid_reason: null | string | number;
    /** 门店ID */
    wm_poi_id: number;
    /** 收货人电话 */
    recipient_phone: string;
    order_time: number;
    wm_poi_order_dayseq: number;
    /** 催单回复内容 */
    response_content: string | null;
    /** 催单回复时间戳 */
    response_time: number;
    /** 催单回复时间字符串  格式 2016-01-03 18:12 */
    response_time_fmt: string;
    /** 收货人地址 */
    recipient_address: string;
};

/**
 * 订单餐损赔付信息
 */
type OrderLossCompensateInfo = {
    /** 申请原因 */
    applyReason: null | string;
    /** 申诉状态发生改变的时间 */
    operatorTime: number;
    orderViewId: string;
    promptTextForCansun: string;
    /** 拒绝原因 */
    refuseReason: null | string;
    /** 可进行申请/申诉的倒计时 */
    remainTime: number;
    /** 申诉状态 */
    statusCode: number;

    operatorId: number;
    operatorName: null | string;
    operatorType: number;
    cantRefundStatus: number;
    statusDes: null | string;
};

/** 定责赔付日志记录 */
type OrderDutyCompensateRecord = {
    canComplain: number;
    ctime: number;
    /** USER(1),POI(2),SYSTEM(5),POI_SERVICE(7),HQ_MANUAL(8); */
    judgeParty: number;
    periodDesc: string;
    periodId: string;
    picUrls: string | string[] | null;
    remark: string | null;
    /** '0未知 1用户 2商家 3商服 4客服 5总部人工 6系统 7天气 8骑手 9美团  (订单中心目前只返回 0，1，2，7，9 骑手会被转成美图返回)'; */
    responsibleParty: number;
    responsiblePartyDesc: string;
    status: number;
    staySeconds: number;
    wmOrderViewId: number;
    wmPoiId: number;
};

type OrderDutyCompensateInfo = OrderDutyCompensateRecord[];

/** 评价骑手结果 */
type DispatcherEvaluationResult = {
    /** 商家输入的评价文案 */
    personal: string;
    /** 结构化的评价文案 */
    structureds: string[] | null;
    /** 评价分属 */
    grade: number;
    /** 订单viewId */
    wmOrderViewId: string;
};

/** 订单退款操作记录 */
type OrderRefundRecord = {
    /** 是否为部分退款 */
    part_refund: 0 | 1;
    refund_marking: number;
    /** 申请类型code 0：订单取消自动确认退款，1：顾客申请，2：客服发起，3：重复提交而自动退款，4：支付成功消息在订单取消之后到达而自动申请，5：支付成功消息在订单被置为无效之后到达而自动申请，6：顾客申请客服介入，7商家申请部分退款 */
    apply_type: number;
    deal_time: number;
    /** 处理类型code 0：等待处理，1：商家拒绝，2：商家同意，3：客户拒绝，4：客服同意，5：超时自动退款，6：系统自动通过，7：顾客取消退款申请，8：顾客取消退款申诉 */
    res_type: number;
    res_reason: string;
    refundFoodItemInfoList?: Array<{
        count: number;
        food_name: string;
        refund_price: number;
    }>;
    pictures?: string[];
    /** 格式化的发起申请时间 */
    apply_time_fmt: string;
    res_time_fmt: string;
    dealHour: number;
    reasonLabel?: string;
    apply_reason: string;
    money: number;
    process_deadline: number;
    current_time: number;
    refundSuggestions?: {
        title: string;
        detail: string[];
        /** 文本中超链接信息 */
        spec?: {
            /** 超链接对应文案 */
            specName: string;
            /** 超链接地址 */
            specUrl: string;
        };
    };
    /** 餐损险 是否展示退款同意提示 */
    showInsuranceAdvice?: boolean;
    /** 拼好饭拼单-订单完成前主单操作提示 */
    phfOperationContent: string;
    /** 拼好饭拼单-订单完成后主单履约服务费提示文案 */
    phfAgreeRefundContent: string;
    /** 拼好饭拼单退款时机：1-完成前退款，2-完成后退款，0-非拼好饭订单默认值，不要使用，无业务含义 */
    phfRefundType: number;
};

/**
 * 跑腿商家【门店维度】权益信息
 */
type PaotuiPoiRightsInfo = {
    /** 权益图标 */
    buttonImg: string | null;
    /** 权益图标 */
    tipsIcon: string | null;
    /** 权益文案 */
    tipsContent: string | null;
    /** 权益文案颜色 */
    tipsColor: string | null;
    /** 权益文案背景颜色 */
    tipsBgColor: string | null;
    /** 权益描述浮层文案 */
    tipsComment: string | null;
    /** 资源类型  0.资源位 1.权益*/
    type: 0 | 1;
};

/**
 * 跑腿商家【订单维度】权益信息
 */
type PaotuiOrderRightsInfo = {
    orderViewId: string | null;
    /** 权益图标 */
    rightsIcon: string | null;
    /** 权益文案 */
    rightsContent: string | null;
    /** 文案颜色 */
    rightsColor: string | null;
    /** 文案背景颜色 */
    rightsBgColor: string | null;
    /** 生效状态  */
    rightValid: boolean;
};

/** 订单退款信息 */
type OrderRefundInfo = OrderRefundRecord[];

/**
 * PUSH 消息里卡餐上报场景类型
 */
enum MakeMealSlowPushSceneType {
    /** 大额订单 */
    BIG_ORDER = 1,
    /** 多sku订单 */
    MULTI_SKU = 2,
    /** 短时间内进单量过大 */
    SHARK_ORDER = 3,
    /** 已识别出卡餐（骑手在店内等餐时间过长） */
    RIDER_WAIT = 4,
    /** 模型v2大额订单 */
    BIG_ORDER_V2 = 10,
    /** 模型v2多sku订单 */
    MULTI_SKU_V2 = 20,
    /** 其他卡餐场景 */
    OTHER = 30,
}

/** 出餐慢类型枚举 */
enum MakeMealSlowType {
    /** 非出餐慢 */
    NONE = 0,
    /** SLA 商家未准时出餐 */
    SLA_POI_NOT_ON_TIME = 1,
    /** 骑手上报出餐慢 */
    DISPATCHER_REPORT = 2,
}

/** 出餐慢申诉状态 */
enum MakeMealSlowAppealStatus {
    /** 默认值 */
    DEFAULT = 0,
    /** 未申诉 */
    NOT_APPEALED = 1,
    /** 已申诉 */
    APPEALED = 2,
    /** 超时未申诉 */
    NOT_APPEALED_OVER_TIME = 3,
}

/** 出餐慢申诉记录 */
type AppealRecord = {
    appealStep: number;
    /** 申诉图片列表 */
    appealPictureList: string[];
    /** 申诉未通过原因 */
    appealResultReason: string;
    /** 申诉过程描述 */
    appealStepDetail: string;
    appealRecordTime: string;
};

/**
 * 商家出餐慢申诉信息
 */
type OrderMakeMealSlowAppealInfo = {
    appealStatusInfo: {
        /** 出餐慢类型 */
        mealSlowType: MakeMealSlowType;
        /** 出餐慢申诉状态 */
        orderAppealStatus: MakeMealSlowAppealStatus;
        /** 申诉时效（s） */
        orderAppealTimeout: number;
        /** 当前申诉步骤提示文案 */
        orderAppealNoticeContent: string;
        /** 骑手上报出餐慢的时间 */
        riderNoticeMealSlowTime: number;
    } | null;
    appealRecords: AppealRecord[] | null;
};

/** 餐损险保险状态枚举 */
enum InsuranceStatusCode {
    /** 未知状态 兜底 */
    UNKNOWN = 0,
    /** 理赔中 */
    CLAIMING = 100,
    /** 理赔成功 */
    CLAIM_SUCCESS = 200,
    /** 理赔失败 */
    CLAIM_FAIL = 300,
}

/**
 * 卡餐上报弹框的展示类型，和[上报卡餐接口](https://km.sankuai.com/page/735683189#id-1%E3%80%81%E6%96%B0%E5%8D%A1%E9%A4%90%E4%B8%8A%E6%8A%A5%E6%8E%A5%E5%8F%A3(%E4%B8%8B%E6%B2%89%E7%BD%91%E5%85%B3%EF%BC%8C%E5%8E%9F/w/report/waitMeal))里的 sceneType 字段保持一致
 */
enum ReportMakeMealSlowModalDisplayType {
    /** 大额订单 */
    BIG_ORDER = 1,
    /** 多sku订单 */
    MULTI_SKU = 2,
    /** 短时间内进单量过大 */
    SHARK_ORDER = 3,
    /** 已识别出卡餐（骑手在店内等餐时间过长） */
    RIDER_WAIT = 4,
    /** 主动上报 */
    MANUAL_REPORT = 5,
    /** 其他卡餐场景 */
    OTHER = 30,
}

/** 卡餐push消息里包含的订单信息 */
type MakeMealSlowPushOrderInfo = {
    /** 订单流水号 */
    orderSeq: number;
    /** 预计送达时间戳（单位：秒） */
    estimateArrivalTime: number;
    /** 商品种类数量 */
    skuTypeCount: number;
    /** 商品数量 */
    skuCount: number;
    /** 预计收入 */
    settleAmount: string;
    /** 超时时间，上报卡餐时需要 */
    expireTime: string;
    /** 来自push的订单ID，用于埋点上报 */
    orderId: string;
    /** 待出餐个数 */
    prepMealOrderCount: number;
    /** 待出餐流水号拼串 */
    prepMealOrderSeqList: string;
    /** 来自Push的推荐延长出餐时间数值 */
    recommendEffectTimeValue: number;
    /** 后端模型灰度标识，push上报卡餐时传回去 */
    balanceUuid: string;
};

/** 餐损险保障详情信息 */
type InsuranceRefundAdviceType = {
    /** 标题 */
    title: string;
    /** 内容 */
    content: Array<{
        label: string;
        value: string;
    }>;
    /** 详情表格图片 */
    refundRule: {
        /** 标题 */
        name: string;
        /** 图片地址 */
        detailPic: string;
    };
    /** 保障条款地址 */
    specUrl: string;
};
/** 客服赔付记录 */
type OrderCustomerServiceCompensateRecord = {
    /** 状态，1:待申请，2:审核中，3:已完成，4:申请超时 */
    status: number;
    /** 状态描述文案 */
    statusDesc: string;
    /** 解释文案 */
    content: string;
    /** 日志时间 */
    operationTime: string;
};

/** 客服赔付 (拼好饭拼单 主单/子单) */
type OrderCustomerServiceCompensateInfo = {
    orderViewIDStr: string;
    /** 申请倒计时(剩余秒数)。仅待申请状态有值，其他状态返回0 */
    expireTime: number;
    /** 赔付日志 */
    compensationItemList: OrderCustomerServiceCompensateRecord[];
};

/** 订单类型（用户下单维度） */
enum WmOrderType {
    /** 普通外卖订单 */
    ORDINARY = 10,
    /** 拼好饭拼单-主单 */
    PHF_MAIN = 20,
    /** 拼好饭拼单-子单 */
    PHF_SUB = 30,
}

/** 拼好饭订单信息 */
type PhfOrderInfo = {
    /**订单流水号 */
    dayNo: string;
    /** 订单View ID字符串 */
    orderViewIDStr: string;
};

/** 1 代表是跨店拼订单，0不是 */
enum OrderBusiness {
    NOT_CROSS = 0,
    CROSS = 1,
}

/**
 * 拼好饭拼单相关信息，主要包含该拼单涉及的主单与子单列表
 */
type PhfOrderVO = {
    /** 订单卡片标题 */
    phfOrderTitle: string;
    /** 子单接单提示文案 */
    phfOrderAcceptHint: string;
    /** 主单信息 */
    mainOrderVO: PhfOrderInfo;
    /** 子单信息列表 */
    subOrderVOList: PhfOrderInfo[];
};

enum OrderTagCode {
    /** 无标签 */
    NONE = 0,
    /** 全城送 */
    QUAN_CHENG = 1,
    /** 到店自取 */
    SELF_PICK = 2,
    /** 大额到付 */
    DA_E_DAO_FU = 3,
    /** 大订单 */
    BIG_ORDER = 4,
    /** 大额订单 */
    DA_E = 5,
    /** 货到付款 */
    PAY_ON_RECEIVE = 6,
    /** 环保单 */
    ECO = 7,
}

enum ShippingCapacityCode {
    /** 锐跑 */
    RUI_PAO = 5,
}

enum BizTagSetCode {
    /** 未知 */
    UNKOWNED = -1,
    /** 出餐慢 */
    MAKING_SLOW = 0,

    /** 团餐订单的主单 */
    GROUP_MEAL_MAIN_ORDER = 1,

    /** 扫码配送 */
    DELIVERY_BY_SCAN_CODE_ORDER = 2,

    /** 无人机配送 */
    UAV_DELIVERY_ORDER = 3,
    /** 卡餐 */
    REPORT_STUCK = 4,
    /** 锐跑订单 */
    RUIPAO_ORDER = 5,
}

interface BasicVO {
    /** 是否完成备餐 1是 0否 */
    isfoodDone: 0 | 1;

    /** 是否显示备餐完成模块 1显示 0不显示 */
    isShowFoodDoneBlock: 0 | 1;

    /** 是否扫码枪出餐 */
    foodSendDeviceInfo?: string;

    /** 出餐时间unix时间戳 */
    foodDoneTime: number;

    /** 出餐状态 */
    foodDoneStatus: FoodDoneStatus;

    /** 出餐规定时间 */
    limitFoodTime: number;
}

interface ButtonVO {
    /** 出餐完成 */
    buttonTitle: string;

    /** 骑手xxx */
    buttonContent: string | null;

    /** 是否展示 */
    isShow: 0 | 1;

    /** 可点击时间 */
    canClickTime: number;
}

interface ContentVO {
    topContentVO: {
        /** 待出餐, 已出餐... */
        title: string;
        /** List标签，顺序显示 */
        titleLabel:
            | {
                  value: string;
                  type: number;
              }[]
            | null;
    };

    subContentVO: {
        /** 规定时间 */
        timeContent: string;
        /** tip */
        tip: string;
        /** 出餐状态 */
        content: string;
        /** List标签 */
        subLabel:
            | {
                  value: string;
                  type: number;
              }[]
            | null;
    };

    /** 比较方式 */
    compareMethod?: string | null;

    /** 开始时间 */
    startTime: number;

    /** 结束时间 */
    endTime: number;

    /** 是否展示计时器 */
    timerShow: 0 | 1;
}

/** @description 出餐状态 */
enum FoodDoneStatus {
    /** 待出餐 */
    WAITING,
    /** 已出餐 */
    SERVED,
    /** 未出餐 */
    NO_SERVE,
}

/**
 * **外卖、拼好饭拆分前的订单基本信息**
 *
 * FIXME: 为了支持灰度，保留了 orderType 与 phfOrderVo 定义，待全量拆分后，彻底移除相关定义
 */

interface OrderSharedBaseInfo {
    // 6.64 新增
    serverTime: number;
    preOrderFoodDoneVO: {
        preOrderGrayTag: boolean;
        canClickLimitTime: number;
        clickWarningToast: string;
    };
    // 是否聚好送
    jhsOrder: boolean;
    businessType: OrderBusinessType;
    utime: number;
    hide_flag: boolean;
    agent_or_direct_poi: number;
    total_items: number;
    /** 订单备注 */
    remark: string;
    /** 预订单的送达时间字符串（形如 2018-12-28 12:34:56） */
    delivery_btime_fmt: string;
    /** 订单支付类型  */
    wm_order_pay_type: PayTypeCode;
    /** 收货人姓名 */
    recipient_name: string;
    /** 备用隐私号列表 */
    backup_privacy_phones: string[];
    /** 是否用户在门店的首单 */
    is_poi_first_order: boolean;
    shipping_service: number;
    /** 订单是否开了发票 0:没有开发票, 1:开了发票 */
    has_been_invoiced: number;
    /** 预计出餐耗时 */
    estimated_make_time: number;
    /** 下单时间戳(秒) */
    order_time: number;
    /** 包裹列表 */
    cartDetailVos: CartDetail[];
    showPrePhoneType: number;
    /** 门店名称 */
    poi_name: string;
    /** 门店ID */
    wm_poi_id: number;
    /** 隐私号状态 */
    privacy_phone_status: PrivacyPhoneStatusCode;
    /** 预定人标签信息 */
    pre_tag: string;
    /** 订单来源 */
    order_source: number;
    /** 订单是否开启隐私号 */
    is_use_privacy_phone: boolean;
    /** 预定人隐私号状态 */
    order_privacy_phone_status: number;
    order_time_fmt: string;
    /** 配送类型code */
    logistics_code: LogisticsTypeCode;
    /** 顾客备用电话 */
    recipient_bindedPhone: string;
    /** 是否预订单 0:否，1:是 */
    is_pre_order: number;
    /** 订单状态 */
    status: OrderStatusCode;
    total_before: number;
    /** 隐私号 格式如"12314214353,231" */
    privacy_phone: string;
    /** 配送费 */
    shipping_fee: number;
    /** 当前系统时间戳 (秒) */
    currentSystemTime: number;
    /** 顾客电话 */
    recipient_phone: string;
    /** 预定人原始电话 如果人没开启隐私号或者隐私号绑定失败，本字段有值，是真实号码，否则没值 */
    order_phone: string;
    /** 订单展示 ID */
    wm_order_id_view: number;
    /** 是否 SLA 门店 */
    poiUseSLA: boolean;
    /** 是否默认隐藏收货地址 */
    hide_address_flag: boolean;
    /** 复制订单时使用的内容 */
    orderCopyContent: string;
    /** 顾客地址 */
    recipient_address: string;
    /** 收餐地址坐标（经度） */
    address_longitude: number;
    /** 订单展示 ID 字符串 */
    wm_order_id_view_str: string;
    /** 商家确定订单时间戳(秒) */
    confirm_time: number;
    /** 配送状态 */
    logistics_status: LogisticsStatusCode;
    /** 包裹数量 */
    cartCount: number;
    /** 是否隐藏顾客距离 */
    mask_address: boolean;
    /** 是否展示备用电话 */
    is_display_bindedPhone: boolean;
    total_after: number;
    /** 即时单的送达时间字符串 （形如 2018-12-28 12:34:56） */
    estimate_arrival_time_fmt: string;
    /** 支付状态 */
    pay_status: PayStatusCode;
    /** 门店坐标（纬度） */
    poi_latitude: number;
    /** 门店坐标（经度） */
    poi_longitude: number;
    id: number;
    poi_city_id: number;
    /** 是否跨天(明天及之后)预订单 0:否，1:是 */
    is_cross_day_pre_order: number;
    /** 收餐地址坐标（纬度） */
    address_latitude: number;
    /** 门店接单方式 */
    self_service: SelfServiceCode;
    discounts: any[];
    /** 部分退款按钮在时间限制下能否展示 */
    part_refund_time_limit: boolean;
    /** 用户 ID */
    user_id: number;
    /** 活动信息 */
    activity: ActivityDetail[];
    /** 配送开始时间 */
    delivery_btime: number;
    /** 发票纳税人 ID */
    invoice_taxpayer_id: string;
    /** 发票抬头 */
    invoice_title: string;
    /** 是否需要餐具 */
    need_tableware: boolean;
    /** 餐盒费 */
    boxPriceTotal: number;
    /** 备用隐私号列表 */
    order_backup_privacy_phones: null | string[];
    encrypted: number;
    /** 打包袋 */
    package_bag_money: number;
    /** 订单序号 */
    num: number;
    /** 隐私号 */
    order_privacy_phone: string;
    tips: string[];
    /** 取餐方式类型 */
    pick_type: PickTypeCode;
    /** 是否为团餐订单 */
    groupOrder: boolean;
    /** 出餐慢申诉信息 */
    appealInfo?: OrderMakeMealSlowAppealInfo;
    /** 餐损险信息 */
    insurance?: OrderInsuranceInfo;
    /** 订单类型 */
    orderType: WmOrderType;
    /** 是否是大订单  0:否, 1:是 */
    isBigOrder: 0 | 1;
    /** 预计送达摘要前缀 */
    expectPrefix: string;
    /** 预计送达时间 */
    expectTimeFmt: string;
    /** 预计送达摘要后缀 */
    expectSuffix: string;
    /** 订单标签 code */
    orderTag: OrderTagCode;
    /** 手机号类型 1：大陆；2：港澳台 */
    recipient_phone_type: 1 | 2;
    /** 是否展示地址栏 */
    isShowDistanceAndAddress: 0 | 1;
    /** 是否展示im入口 */
    orderImValid: 0 | 1;
    /** 延时完单提示 */
    completeOrderNotice: string;
    /**商家灰度回流字段 */
    selfDeliveryBackflowGray: string;
    /** 订单 配送支持运力方式 */
    shippingCapacityCodes: ShippingCapacityCode[];
    /** 业务打标字段，由订单中心透传 */
    bizSetTag: BizTagSetCode[];
    /** 是否为跨店拼单 */
    orderBusiness: OrderBusiness;
    /**
     * @description 出餐新增考核, 可能会为空值
     * @documentation https://km.sankuai.com/collabpage/1898306897
     */
    mealFoodDoneVO: {
        basicVO: BasicVO;
        buttonVO: ButtonVO | null;
        contentVOList: ContentVO[];
    };
    /** 是否为拼单宝 */
    pdbOrder: 0 | 1;
    orderCardTopDisplayVO: {
        subTitlePrompt: string;
    };
}
interface OrdinaryOrderBaseInfo extends OrderSharedBaseInfo {
    /** 订单类型 */
    orderType: WmOrderType.ORDINARY;
}
interface PhfMainOrderBaseInfo extends OrderSharedBaseInfo {
    /** 订单类型 */
    orderType: WmOrderType.PHF_MAIN;
    phfOrderVO: PhfOrderVO;
}

interface PhfSubOrderBaseInfo extends OrderSharedBaseInfo {
    /** 订单类型 */
    orderType: WmOrderType.PHF_SUB;
    phfOrderVO: PhfOrderVO;
}

/** 用户提单时的配送方式 */
enum ShippingService {
    /** 默认值 */
    DEFAULT = 0,
    /** 商家自配 */
    ZIPEI = 1,
    /** 美团专送 */
    ZHUAN_Z = 1010,
    /** 美团快送 */
    KUAISONG = 1020,
    /** 美团混合送 */
    HUNHE = 1030,
    /** 美团全城送 */
    QUANCHENG = 1040,
    /** 众包服务 */
    ZHONGBAO = 2010,
    /** 三方配送 */
    SANFANG = 300,
    /** 企客-全城送 */
    QIKE_CITY = 4015,
}

type OrderBaseInfo = OrdinaryOrderBaseInfo | PhfMainOrderBaseInfo | PhfSubOrderBaseInfo;

/** 跨店送订单按钮类型 */
enum CrossOrderCardBtnType {
    // 接单
    ConFirmOrder = 0,
    // 出餐
    FoodDone = 1,
    // 配送
    Distribute = 2,
    // 转自配
    ChangeSelfDistribute = 3,
    // 取消配送
    CancelDistribute = 4,
}

/** 推断数组中的元素 */
type ArrayElement<
    ArrayType extends readonly unknown[]
> = ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

// TODO: 可扩展更多OrderBlockTag类型
/** 用于实现细粒度更新 */
enum OrderBlockTag {
    mealFoodDoneVO = 'mealFoodDoneVO',
}

/** UI类别, 可扩展 */
enum CardType {
    'card' = 'card',
    'ordinary' = 'ordinary',
}

export {
    UnprocessedOrderType,
    PrivacyPhoneStatusCode,
    OrderStatusCode,
    LogisticsStatusCode,
    PayStatusCode,
    OrderBaseInfo,
    OrderDeliveryInfo,
    LogisticsTypeCode,
    PickTypeCode,
    OrderPrepareStatusCode,
    OrderOtherInfo,
    OrderUserInfo,
    OrderDistanceInfo,
    OrderImgInfo,
    SelfServiceCode,
    PayTypeCode,
    OrderChargeInfo,
    CartDetail,
    SettlementItem,
    OrderUserCompensateInfo,
    SharedUserCompensateRule,
    OrderRemindRecord,
    OrderLossCompensateInfo,
    OrderDutyCompensateRecord,
    OrderDutyCompensateInfo,
    Distributor,
    DispatcherEvaluationResult,
    OrderRefundRecord,
    OrderRefundInfo,
    RefundTips,
    OrderMakeMealSlowAppealInfo,
    MakeMealSlowType,
    MakeMealSlowAppealStatus,
    MakeMealSlowPushSceneType,
    ReportMakeMealSlowModalDisplayType,
    MakeMealSlowPushOrderInfo,
    PaotuiPoiRightsInfo,
    PaotuiOrderRightsInfo,
    InsuranceRefundAdviceType,
    OrderCustomerServiceCompensateInfo,
    InsuranceStatusCode,
    OrderBusinessType,
    OrderCommonInfo,
    RawOrderBaseInfo,
    WmOrderType,
    PhfMainOrderBaseInfo,
    PhfSubOrderBaseInfo,
    PhfOrderInfo,
    PhfOrderVO,
    OrderTagCode,
    OrdinaryOrderBaseInfo,
    ShippingCapacityCode,
    OrderBusiness,
    CrossOrderCardBtnType,
    ShippingService,
    ContentVO,
    FoodDoneStatus,
    ArrayElement,
    BasicVO,
    ButtonVO,
    OrderBlockTag,
    CardType,
};
